import React, { useState, useEffect } from "react";
import { Alert, Button, Card, Form } from "react-bootstrap";
import { faPaperPlane } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNavigate, useParams } from "react-router-dom";
import { Formik } from "formik";
import * as Yup from "yup";
import api from "../../../service/api";
import { AddEditQuotationDetails } from "./AddEditQuotationDetails";
import { AddEditQuotationItems } from "./AddEditQuotationItems";
import { AddEditBilling } from "./AddEditBilling";

export const AddEditQuotation = ({ project }) => {
    const navigate = useNavigate();
    let { id, action } = useParams();

    const schema = Yup.object().shape({
        quotation_date: Yup.string().required(),
        quotation_to: Yup.string().required(),
        quotation_from: Yup.string().required(),
        total: Yup.string().required(),
        quotation_items: Yup.array().of(
            Yup.object().shape({
                item: Yup.string().required(),
                description: Yup.string().required(),
                quantity: Yup.string().required(),
                rate: Yup.string().required(),
                amount: Yup.string().required(),
            })
        ),
    });

    //
    // States
    //

    const [editExpenses, setEditExpenses] = useState();
    // eslint-disable-next-line no-unused-vars
    const [notif, setNotif] = useState({
        notifMsg: "",
        open: false,
        severity: "success",
    });
    const [inputFields, setInputFields] = useState({
        quotation_date: "",
        quotation_to: "fonda",
        quotation_from: "intellect",
        gst: "7",
        discount: 0,
        subtotal: 0,
        total: 0,
        quotation_items: [
            {
                item: "",
                description: "",
                quantity: "",
                rate: "",
                amount: 0,
            },
        ],
    });

    //
    // Functions
    //

    const addFields = () => {
        let newfield = {
            item: "",
            description: "",
            quantity: "",
            rate: "",
            amount: 0,
        };

        setInputFields({
            ...inputFields,
            quotation_items: [...inputFields.quotation_items, newfield],
        });
    };

    const removeFields = (index) => {
        let data = [...inputFields.quotation_items];
        data.splice(index, 1);

        calculateRemovedFields(data);

        setInputFields({
            ...inputFields,
            quotation_items: data,
        });
    };

    const handleFormChangeItems = (index, event) => {
        let data = [...inputFields.quotation_items];
        data[index][event.target.name] = event.target.value;

        calculateTotalAmount(index, event);

        setInputFields({
            ...inputFields,
            quotation_items: data,
        });
    };

    // const handleFormChangeDetails = (event) => {
    //     setInputFields({
    //         ...inputFields,
    //         quotation_date: event.target.value,
    //     });
    // };

    const handleFormChangeDiscount = (event) => {
        inputFields[event.target.name] = event.target.value;

        let quotationItems = [...inputFields.quotation_items];
        let quotationItemsTotal = quotationItems.reduce(
            (total, current) => +total + +current.amount,
            0
        );

        const finalTotal = computeFinalTotal(quotationItemsTotal);

        inputFields["total"] = finalTotal > 0 ? finalTotal : 0;

        setInputFields({
            ...inputFields,
        });
    };

    const calculateTotalAmount = (index, event = null) => {
        let data = [...inputFields.quotation_items];
        data[index][event.target.name] = event.target.value;

        const itemTotalAmount =
            inputFields["quotation_items"][index]["quantity"] *
            inputFields["quotation_items"][index]["rate"];

        inputFields["quotation_items"][index]["amount"] = itemTotalAmount;

        let quotationItemsTotal = data.reduce(
            (total, current) => +total + +current.amount,
            0
        );

        const finalTotal = computeFinalTotal(quotationItemsTotal);

        inputFields["subtotal"] = quotationItemsTotal;
        inputFields["total"] = finalTotal > 0 ? finalTotal : 0;
    };

    const calculateRemovedFields = (data) => {
        let quotationItemsTotal = data.reduce(
            (total, current) => +total + +current.amount,
            0
        );

        const finalTotal = computeFinalTotal(quotationItemsTotal);

        inputFields["total"] = finalTotal > 0 ? finalTotal : 0;
    };

    const computeFinalTotal = (total) => {
        const gstRate = 7;

        return (gstRate / 100) * total + total - inputFields["discount"];
    };

    const submitCreateQuotation = (values) => {
        values["portfolio_id"] = project.portfolio_id;
        values["programme_id"] = project.programme_id;
        values["project_id"] = id;

        return api.post("quotations", values);
    };

    const submitEditQuotation = (values) => {
        try {
            return api.put(`quotations/${action}`, values);
        } catch {
            setNotif({
                notifMsg: "Something went wrong with the server",
                open: true,
                severity: "danger",
            });
        }
    };

    //
    // useEffects
    //

    useEffect(() => {
        if (action !== undefined && action !== "create") {
            const getQuotation = async () => {
                try {
                    const response = await api.get(`quotations/${action}`, {});

                    setEditExpenses(response.data);
                } catch (error) {
                    setNotif({
                        notifMsg: "Something went wrong with the server",
                        open: true,
                        severity: "danger",
                    });
                }
            };
            getQuotation();
        }
    }, [action]);

    useEffect(() => {
        if (editExpenses) {
            setInputFields({
                quotation_date: editExpenses.quotation_date,
                quotation_to: editExpenses.quotation_to,
                quotation_from: editExpenses.quotation_from,
                gst: editExpenses.gst,
                discount: editExpenses.discount,
                subtotal: editExpenses.subtotal,
                total: editExpenses.total,
                quotation_items: editExpenses.quotation_items,
            });
        }
    }, [editExpenses]);

    return (
        <Card className="shadow-none mb-0">
            <Card.Header className="shadow-none pb-0">
                <Card.Title tag="h5">
                    {action === "create" ? "Create" : "Edit"} quotation
                </Card.Title>
            </Card.Header>
            <Card.Body className="pb-0">
                <Formik
                    enableReinitialize
                    initialValues={inputFields}
                    validationSchema={schema}
                    onSubmit={async (
                        values,
                        { setErrors, setStatus, setSubmitting }
                    ) => {
                        try {
                            action === "create"
                                ? await submitCreateQuotation(values)
                                : await submitEditQuotation(values);

                            navigate(`/intellidocs/process/quotations/${id}`, {
                                state: {
                                    open: true,
                                    notifMsg: `Successfully ${
                                        action === "create"
                                            ? "created"
                                            : "updated"
                                    } quotation`,
                                    severity: "success",
                                },
                            });
                        } catch (error) {
                            const message =
                                error.message || "Something went wrong";

                            setStatus({ success: false });
                            setErrors({ submit: message });
                            setSubmitting(false);
                            setNotif({
                                notifMsg:
                                    "Something went wrong with the server",
                                open: true,
                                severity: "danger",
                            });
                        }
                    }}
                >
                    {({
                        errors,
                        handleBlur,
                        handleChange,
                        handleSubmit,
                        touched,
                        values,
                    }) => (
                        <Form noValidate onSubmit={handleSubmit}>
                            {errors.submit && (
                                <Alert className="my-3" variant="danger">
                                    <div className="alert-message">
                                        {errors.submit}
                                    </div>
                                </Alert>
                            )}
                            <h6>Quotation items</h6>
                            <div className="bg-light p-4 rounded mb-3">
                                <AddEditQuotationItems
                                    values={values}
                                    errors={errors}
                                    touched={touched}
                                    handleBlur={handleBlur}
                                    handleFormChangeItems={
                                        handleFormChangeItems
                                    }
                                    removeFields={removeFields}
                                    addFields={addFields}
                                    handleFormChangeDiscount={
                                        handleFormChangeDiscount
                                    }
                                />
                            </div>
                            <h6>Quotation details</h6>
                            <div className="bg-light p-4 rounded mb-3">
                                <AddEditQuotationDetails
                                    values={values}
                                    errors={errors}
                                    touched={touched}
                                    handleBlur={handleBlur}
                                    handleFormChangeDetails={handleChange}
                                />
                            </div>
                            <h6>Billing details</h6>
                            <div className="bg-light p-4 rounded">
                                <AddEditBilling project={project} />
                            </div>
                            <Card.Footer className="text-center pb-0">
                                <Button
                                    variant="primary"
                                    type="submit"
                                    className="me-2"
                                    size="lg"
                                >
                                    <FontAwesomeIcon icon={faPaperPlane} /> Save
                                </Button>
                                <Button
                                    variant="link"
                                    onClick={() =>
                                        navigate(
                                            `/intellidocs/process/quotations/${id}`
                                        )
                                    }
                                >
                                    Cancel
                                </Button>
                            </Card.Footer>
                        </Form>
                    )}
                </Formik>
            </Card.Body>
        </Card>
    );
};
